/**
 * 파일 저장 — 스탠드얼론은 디스크에 직접, 웹은 내려받기.
 *
 * 스탠드얼론에서도 `<a download>` 를 쓰던 시절에는 Electron 이 매번 저장 대화상자를 띄워, 이미 저장한
 * 프로젝트를 Ctrl+S 해도 위치를 다시 물었다(신고 2026-10-01). 이제 한 번 저장(또는 열기)한 파일은
 * `diskPath` 를 기억해 두고 그대로 덮어쓴다. 어떤 경로에 쓸 수 있는지는 main 이 정한다
 * (electron/main.js 「파일 저장」) — 여기서 경로를 지어내 쓸 길은 없다.
 *
 * 돌려주는 값:
 *   { ok: true, path }              디스크에 썼다(path = 실제 경로)
 *   { ok: true, path: null }        웹 — 내려받기를 시작했다(끝났는지는 브라우저만 안다)
 *   { ok: false, canceled: true }   사용자가 대화상자를 닫았다 — 「저장됨」 으로 표시하면 안 된다
 *   { ok: false, reason, detail }   실패
 */

const fileApi = () => (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.file) || null;

/** 디스크에 직접 쓸 수 있는가(스탠드얼론) */
export const canWriteDisk = () => !!fileApi();

/** 경로의 파일 이름(확장자 제외) — 프로젝트 이름을 고른 파일 이름으로 맞출 때 */
export const baseNameOf = (p) => {
    const name = String(p || '').split(/[\\/]/).pop() || '';
    return name.replace(/\.[^.]+$/, '');
};

function download(fileName, content, mime) {
    const blob = new Blob([content], { type: mime });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
}

/**
 * @param {object} o
 * @param {string} o.fileName   제안할 파일 이름(확장자 포함)
 * @param {string} o.content
 * @param {'project'|'sql'} o.kind
 * @param {string} [o.diskPath] 이미 저장한 적 있는 경로 — 있으면 묻지 않고 덮어쓴다
 * @param {boolean} [o.saveAs]  「다른 이름으로」 — diskPath 가 있어도 묻는다
 */
export async function saveTextFile({ fileName, content, kind, diskPath, saveAs = false }) {
    const api = fileApi();
    if (!api) {
        download(fileName, content, kind === 'project' ? 'application/json;charset=utf-8' : 'text/plain;charset=utf-8');
        return { ok: true, path: null };
    }
    if (diskPath && !saveAs) {
        const r = await api.save({ path: diskPath, content });
        // 고른 적 없는 경로(다른 PC 에서 옮겨 온 상태 등)면 위치를 묻는다 — 실패로 끝내지 않는다
        if (r && (r.ok || r.reason !== 'NOT_APPROVED')) return r;
    }
    return api.saveAs({ suggestedName: fileName, content, kind });
}

/** 파일 선택 창으로 연 파일의 경로 — 스탠드얼론만. 못 얻으면 null */
export async function rememberOpenedFile(file) {
    const api = fileApi();
    if (!api || !file) return null;
    try {
        const r = await api.rememberOpened(file);
        return (r && r.ok && r.path) || null;
    } catch {
        return null;
    }
}
